import { useState, useEffect } from 'react';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { schedulerApi } from '@/lib/scheduler-api';

export interface ScheduleOutput {
    triggerType: 'one_shot' | 'recurring';
    scheduleKind?: 'delay' | 'cron' | 'rrule';
    cronExpression?: string;
    rruleExpression?: string;
    timezone: string;
    delayUntilAt?: string;
}

interface ScheduleBuilderProps {
    value: ScheduleOutput;
    onChange: (v: ScheduleOutput) => void;
}

const COMMON_TIMEZONES = [
    'UTC', 'America/New_York', 'America/Chicago', 'America/Denver',
    'America/Los_Angeles', 'Europe/London', 'Europe/Paris', 'Europe/Berlin',
    'Asia/Tokyo', 'Asia/Shanghai', 'Australia/Sydney',
];

const CRON_PRESETS = [
    { label: 'Every minute', expr: '* * * * *' },
    { label: 'Every 5 minutes', expr: '*/5 * * * *' },
    { label: 'Every 15 minutes', expr: '*/15 * * * *' },
    { label: 'Hourly', expr: '0 * * * *' },
    { label: 'Daily at midnight', expr: '0 0 * * *' },
    { label: 'Daily at 9am', expr: '0 9 * * *' },
    { label: 'Weekly (Mon 9am)', expr: '0 9 * * 1' },
    { label: 'Monthly (1st at midnight)', expr: '0 0 1 * *' },
];

export function ScheduleBuilder({ value, onChange }: ScheduleBuilderProps) {
    const [preview, setPreview] = useState<string[]>([]);

    useEffect(() => {
        if (value.triggerType !== 'recurring') {
            setPreview([]);
            return;
        }
        const timer = setTimeout(async () => {
            try {
                const res = await schedulerApi.previewSchedule({
                    triggerType: value.triggerType,
                    scheduleKind: value.scheduleKind,
                    cronExpression: value.cronExpression,
                    rruleExpression: value.rruleExpression,
                    timezone: value.timezone,
                    count: 5,
                });
                setPreview(res.data.data?.occurrences ?? []);
            } catch {
                setPreview([]);
            }
        }, 500);
        return () => clearTimeout(timer);
    }, [value.triggerType, value.scheduleKind, value.cronExpression, value.rruleExpression, value.timezone]);

    const updateField = <K extends keyof ScheduleOutput>(key: K, val: ScheduleOutput[K]) => {
        onChange({ ...value, [key]: val });
    };

    return (
        <Card>
            <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium">Schedule</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
                <div className="flex gap-2">
                    <Button
                        type="button"
                        variant={value.triggerType === 'one_shot' ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => onChange({ ...value, triggerType: 'one_shot', scheduleKind: 'delay' })}
                    >
                        Run Once
                    </Button>
                    <Button
                        type="button"
                        variant={value.triggerType === 'recurring' && value.scheduleKind === 'cron' ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => onChange({ ...value, triggerType: 'recurring', scheduleKind: 'cron' })}
                    >
                        Cron
                    </Button>
                    <Button
                        type="button"
                        variant={value.triggerType === 'recurring' && value.scheduleKind === 'rrule' ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => onChange({ ...value, triggerType: 'recurring', scheduleKind: 'rrule' })}
                    >
                        Calendar (RRULE)
                    </Button>
                </div>

                {value.triggerType === 'one_shot' && (
                    <div className="space-y-2">
                        <Label>Run at</Label>
                        <input
                            type="datetime-local"
                            className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm"
                            value={value.delayUntilAt?.slice(0, 16) ?? ''}
                            onChange={(e) => updateField('delayUntilAt', e.target.value ? new Date(e.target.value).toISOString() : undefined)}
                        />
                    </div>
                )}

                {value.triggerType === 'recurring' && value.scheduleKind === 'cron' && (
                    <div className="space-y-2">
                        <Label>Cron Expression</Label>
                        <input
                            type="text"
                            className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm font-mono"
                            placeholder="*/5 * * * *"
                            value={value.cronExpression ?? ''}
                            onChange={(e) => updateField('cronExpression', e.target.value)}
                        />
                        <div className="flex flex-wrap gap-1">
                            {CRON_PRESETS.map((p) => (
                                <Button
                                    key={p.expr}
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    className="text-xs h-6"
                                    onClick={() => updateField('cronExpression', p.expr)}
                                >
                                    {p.label}
                                </Button>
                            ))}
                        </div>
                    </div>
                )}

                {value.triggerType === 'recurring' && value.scheduleKind === 'rrule' && (
                    <div className="space-y-2">
                        <Label>RRULE Expression</Label>
                        <input
                            type="text"
                            className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm font-mono"
                            placeholder="FREQ=WEEKLY;BYDAY=MO,WE,FR;BYHOUR=9;BYMINUTE=0"
                            value={value.rruleExpression ?? ''}
                            onChange={(e) => updateField('rruleExpression', e.target.value)}
                        />
                    </div>
                )}

                <div className="space-y-2">
                    <Label>Timezone</Label>
                    <select
                        className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm"
                        value={value.timezone}
                        onChange={(e) => updateField('timezone', e.target.value)}
                    >
                        {COMMON_TIMEZONES.map((tz) => (
                            <option key={tz} value={tz}>{tz}</option>
                        ))}
                    </select>
                </div>

                {preview.length > 0 && (
                    <div className="space-y-1">
                        <Label className="text-xs text-muted-foreground">Next occurrences</Label>
                        <ul className="text-xs space-y-0.5 text-muted-foreground font-mono">
                            {preview.map((d, i) => (
                                <li key={i}>{new Date(d).toLocaleString()}</li>
                            ))}
                        </ul>
                    </div>
                )}
            </CardContent>
        </Card>
    );
}
